<template>
	<div
		class="widget-preview widget-block-bg"
		:style="{ 'max-height': maxHeight, 'min-height': minHeight, height: isMiniApp ? '190px' : undefined }"
	>
		<div v-if="isMiniApp" class="image-wrapper flex-centerize">
			<MiniAppImage :fieldType="firstWidgetFieldType" />
		</div>
		<WidgetForm
			v-else-if="widget"
			ref="widget_preview_form"
			:class="{ 'opacity-0': childClientHeight === 0 }"
			:isReadOnly="true"
			:isEndRun="isEndRun"
			@onChildUpdated="onChildUpdated"
			:stickyFooter="isTileEditor"
		/>
		<AppLoader class="center-absolutely" :class="{ 'opacity-0': childClientHeight > 0 }" />
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { getWidget } from '@/store/modules/widgets/getters';
import { getWorkflow } from '@/store/modules/workflows/getters';

import { flatPreviousSteps } from '@/store/modules/selectedNode/getters';
import { replaceInsertDataStringDictionary } from '@/store/modules/selectedWorkflow/getters';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { configurationsMappingBlocksUUIDs } from '@/store/modules/selectedWorkflow/modules/configurations/getters';

import WidgetForm from '@/components/WidgetForm';
import MiniAppImage from './MiniAppImage';

import BlockEditorAppSelectService from '@/components/BlockEditor/BlockEditorAppSelect/block-editor-app-select-service';
import WorkflowService from '@/utils/WorkflowService';

export default {
	name: 'WidgetPreview',
	components: {
		WidgetForm,
		MiniAppImage,
	},
	props: {
		widgetId: { type: String },
		isEndRun: { type: Boolean, default: false },
	},
	computed: {
		...mapGettersShorthand({ editWorkflow }),
		...mapGetters([
			getWorkflow.fullPath,
			getWidget.fullPath,
			flatPreviousSteps.fullPath,
			replaceInsertDataStringDictionary.fullPath,
			configurationsMappingBlocksUUIDs.fullPath,
		]),
		widget() {
			return this[getWidget.fullPath](this.widgetId);
		},
		firstWidgetFieldType() {
			return this.widget?.fields?.length > 0 && this.widget?.fields[0].field_type;
		},
		maxHeight() {
			if (this.isMiniApp) {
				return '400px';
			}
			return this.childClientHeight === 0 ? 'inherit' : this.childClientHeight / 2 + 20 + 'px';
		},
		minHeight() {
			return this.isMiniApp ? 'auto  !important' : '50px';
		},
		isMiniApp() {
			return BlockEditorAppSelectService.isMiniApp(this.firstWidgetFieldType);
		},
		isTileEditor() {
			return WorkflowService.isOfTypeTile(this.editWorkflow);
		},
	},
	methods: {
		onChildUpdated() {
			this.$set(this, 'childClientHeight', this.$refs.widget_preview_form?.$el.clientHeight);
		},
		setWidget(widget) {
			const previousSteps = this[flatPreviousSteps.fullPath];
			const replaceInsertDataStringDict = this[replaceInsertDataStringDictionary.fullPath];
			const configurationsMappingBlocksUUIDsValues = this[configurationsMappingBlocksUUIDs.fullPath];
			this.$refs.widget_preview_form?.setWidget({
				widget: Object.assign(
					{},
					{ ...widget },
					{ previousSteps, replaceInsertDataStringDict, configurationsMappingBlocksUUIDs: configurationsMappingBlocksUUIDsValues }
				),
				is_preview: true,
			});
		},
	},
	mounted() {
		if (this.widget) {
			this.setWidget(this.widget);
		}
	},
	watch: {
		widget: {
			handler(newV) {
				if (newV && this.$refs.widget_preview_form) {
					this.$nextTick(() => {
						this.setWidget(newV);
					});
				}
			},
			immediate: true,
		},
	},
	data() {
		return { childClientHeight: 0 };
	},
};
</script>

<style scoped lang="scss">
.widget-preview {
	position: relative;
	width: 290px;
	max-width: 288px;
	min-width: 288px;
	max-height: 270px;
	padding: 10px 0;
	border-top: 1px solid var(--button-border);
	pointer-events: none;
	border-radius: 0;
	border-bottom-left-radius: inherit;
	border-bottom-right-radius: inherit;

	::v-deep(.widget-form) {
		width: 575px;
		padding: 10px 0 !important;
		transform: scale(0.5);
		transform-origin: top left;
		overflow-y: hidden;

		.widgets-container {
			max-height: 400px;
			padding: 20px 40px 0 40px;
			overflow: hidden;
		}
		.widget-form-inner-container {
			padding-right: 40px;
		}

		.widget-form-element {
			width: 80%;
			overflow-y: auto;
			max-height: 300px;
			box-shadow: var(--card-shadow);
		}
	}

	.image-wrapper {
		position: relative; // so loader is hidden
		z-index: 1;
	}
}
</style>
